import { useState, useEffect } from 'react';
import { getDropdownFields, getFieldConfig } from '../services/dropdownApi';
import { getAccessTokenIdentity } from '../utils/accessToken';

function readRequestIdentity() {
  const tokenIdentity = getAccessTokenIdentity();
  return {
    userId: tokenIdentity.userId ?? localStorage.getItem('userId') ?? undefined,
    roleId: tokenIdentity.roleId ?? localStorage.getItem('roleId') ?? undefined,
  };
}

/**
 * Fetches the list of filterable fields for a module.
 * Pass `module=null` to skip fetching (e.g. when drawer is closed).
 */
export function useDropdownFields(module) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!module) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getDropdownFields(module)
      .then((data) => {
        if (!cancelled) setFields(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [module]);

  return { fields, loading, error };
}

export function useFieldConfig(module) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!module) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getFieldConfig(module)
      .then((data) => {
        if (!cancelled) setConfig(Array.isArray(data) ? data : null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [module]);

  return { config, loading, error };
}

/**
 * Fetches filter-specific field config from the auth API (configType=filter),
 * returning only fields where isVisible is true.
 * Falls back to the submissions filter-dropdown-fields endpoint when the auth
 * API returns nothing.
 */
export function useFilterFields(module) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!module) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    // roleId must represent the authenticated token, not a potentially stale
    // or login-response-derived localStorage value.
    const { userId, roleId } = readRequestIdentity();

    const isFieldVisible = (f) => {
      const vis = f?.isVisible ?? f?.is_visible ?? f?.visible;
      if (vis === undefined || vis === null) return true;
      if (typeof vis === 'boolean') return vis;
      if (typeof vis === 'string') return !['false', '0', 'no', 'hide', 'hidden'].includes(vis.toLowerCase());
      return Boolean(vis);
    };

    getFieldConfig(module, { configType: 'filter', userId, roleId })
      .then(async (data) => {
        const rows = Array.isArray(data) ? data : (data?.visibleFields ?? []);
        const visible = rows.filter(isFieldVisible);
        // If the filter config exists but has no visible fields (admin hasn't
        // marked anything as visible yet), fall back to the module's base fields
        // so the user can still filter without an empty drawer.
        if (visible.length > 0) return visible;
        const fallback = await getDropdownFields(module);
        return Array.isArray(fallback) ? fallback.filter(isFieldVisible) : [];
      })
      .then((rows) => {
        if (!cancelled) setFields(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [module]);

  return { fields, loading, error };
}
